import React, { useState, useEffect } from 'react';
import { Box, List, ListItemButton, ListItemText, ListItemIcon, Divider, CircularProgress, Button, Typography } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { ConfluencePageView } from './ConfluencePageView';

interface TrainingPage {
  id: string;
  label: string;
  pageId: string;
  description: string;
}

export const TrainingDocsTab: React.FC<{ initialPageId?: string }> = ({ initialPageId }) => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const [pages, setPages] = useState<TrainingPage[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const baseUrl = configApi.getString('backend.baseUrl');
      const { token } = await identityApi.getCredentials();
      const res = await fetch(`${baseUrl}/api/custom-api/dev-hub/training-pages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      const fetched: TrainingPage[] = data.pages ?? [];
      setPages(fetched);
      // Use initialPageId if provided and valid, otherwise default to first page
      const target = initialPageId && fetched.find(p => p.pageId === initialPageId);
      setSelectedId(target ? target.pageId : fetched[0]?.pageId ?? null);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [initialPageId]);

  if (loading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} sx={{ color: '#1a202c' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
        <Typography sx={{ fontSize: '1.5rem' }}>📄</Typography>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#374151' }}>Couldn't load training docs</Typography>
        <Typography sx={{ fontSize: '0.78rem', color: '#9ca3af' }}>Something went wrong fetching the pages.</Typography>
        <Button size="small" onClick={load} variant="outlined" sx={{ textTransform: 'none', mt: 0.5, borderColor: '#e2e8f0', color: '#4a5568', '&:hover': { borderColor: '#cbd5e0', bgcolor: '#f7fafc' } }}>Try again</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Sidebar */}
      <Box
        sx={{
          width: 240,
          flexShrink: 0,
          borderRight: '1px solid #e5e7eb',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <List dense disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
          {pages.map((page, i) => (
            <React.Fragment key={page.pageId}>
              {i > 0 && <Divider />}
              <ListItemButton
                selected={selectedId === page.pageId}
                onClick={() => setSelectedId(page.pageId)}
                disableRipple
                sx={{
                  py: 1.5,
                  '&.Mui-selected': { bgcolor: '#f5f5f5 !important', borderRight: '3px solid #1a202c' },
                  '&.Mui-selected:hover': { bgcolor: '#eeeeee !important' },
                  '&:hover': { bgcolor: '#f5f5f5' },
                  '& .MuiTouchRipple-root': { display: 'none' },
                }}
              >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                <ArticleIcon sx={{ fontSize: 18, color: selectedId === page.pageId ? '#1a202c' : '#9ca3af' }} />
                </ListItemIcon>
                <ListItemText
                  primary={page.label}
                  secondary={page.description}
                  primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: selectedId === page.pageId ? 700 : 500 }}
                  secondaryTypographyProps={{ fontSize: '0.72rem' }}
                />
              </ListItemButton>
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ConfluencePageView
          pageId={selectedId}
          emptyMessage="Select a training document from the list on the left."
        />
      </Box>
    </Box>
  );
};
